import { is, List } from "immutable";
import * as React from "react";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import * as redux from "redux";
import { EditForm } from "./EditForm";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import { ErrorCodes } from "../../../domain";
import * as Forms from "../../../forms";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { AppState } from "../../../store";
import { ReactHelper, Utility } from "../../../utils";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    ...Comp.Base.DefaultStatePropsMap(state, ownProps),
    templates: state.adminEmailTemplates.templates,
    loadingTypes: state.adminEmailTemplates.loadingTypes,
    types: state.adminEmailTemplates.types,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    ...Comp.Base.DefaultDispatchPropsMap(dispatch),
    loadTypes: () => dispatch(Actions.AdminEmailTemplate.loadTypes()),
    removeTemplate: (template: M.Email.EmailTemplate) => dispatch(Actions.AdminEmailTemplate.removeTemplate(template)),
    toggleLoadingTypes: (loading: boolean) => dispatch(Actions.AdminEmailTemplate.toggleLoadingTypes(loading)),
    updateTemplate: (template: M.Email.EmailTemplate) => dispatch(Actions.AdminEmailTemplate.updateTemplate(template)),
});

type OwnProps = RouteComponentProps<{
    id: string;
}> & {};

type ConnectedState = Comp.Base.DefaultConnectedState & {
    templates: List<M.Email.EmailTemplate>;
    loadingTypes: boolean;
    types: List<M.Email.EmailTemplateType>;
};

type ConnectedDispatch = Comp.Base.DefaultConnectedDispatch & {
    loadTypes: () => void;
    removeTemplate: (template: M.Email.EmailTemplate) => void;
    toggleLoadingTypes: (loading: boolean) => void;
    updateTemplate: (template: M.Email.EmailTemplate) => void;
};

interface OwnState {
    lastPath: string;
    template?: M.Email.EmailTemplate;
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class EditComponent extends Comp.Base.BaseComponent<CombinedProps, OwnState> {

    private _confirmDelete: Comp.Modal.ConfirmModal | null = null;
    private form: EditForm | null = null;

    public constructor(props: CombinedProps) {
        super(props);
        this.state = {
            lastPath: props.lastPath && props.lastPath !== Utility.getRelativePath()
                ? props.lastPath
                : Paths.AdminEmailTemplate.List,
        };
    }

    public async componentDidMount() {
        this._isMounted = true;
        const templateId = this.getRecordId();
        await this.loadTemplateFromState(true);

        if (templateId > 0)
            this.loadTemplate();

        this.props.loadTypes();
    }

    public componentDidUpdate(prevProps: Readonly<CombinedProps>) {
        if (!is(prevProps.templates, this.props.templates))
            this.loadTemplateFromState();
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        const evt = this.state.template;
        const templateId = this.getRecordId();
        const loading = this.state.loading;
        const templateListLink = this.lastPathIsFromListView()
            ? <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); window.history.back(); }}>Email templates</a>
            : <Link to={Paths.AdminEmailTemplate.List}>Email templates</Link>;

        const cancelLink = this.lastPathIsFromListView()
            ? <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); window.history.back(); }}>Cancel</a>
            : <Link to={this.state.lastPath}>Cancel</Link>;

        const cancelWrap = <Comp.Generic.WrapperLess>
            {cancelLink}
            {templateId > 0
                ? <span className="btn btn-link mute" onClick={() => this.toggleDeleteConfirmation()} style={{ float: "right" }}>
                    <Comp.Generic.Icon icon="times" /> Delete
                </span>
                : null}
        </Comp.Generic.WrapperLess>;

        return <Comp.Page.AdminPage title={"EmailTemplates"}>

            {this.getErrorAlert()}

            <Comp.Generic.LoadingSpinner show={loading} />

            {templateId > 0
                ? <Comp.Modal.ConfirmModal
                    headerClassName="warning"
                    onProceed={() => this.handleDelete()}
                    title="Delete template"
                    ref={(ref) => this._confirmDelete = ref}
                >
                    <p>Are you sure you want to delete this template?</p>
                </Comp.Modal.ConfirmModal>
                : null}

            <h1>
                {templateListLink} &raquo;{" "}
                {templateId === 0 ? "Add new template" : `Edit ${evt ? evt.name : "template"}`}
            </h1>

            <EditForm
                api={this.props.api}
                cancelLink={cancelWrap}
                types={this.props.types}
                onSubmit={(model, body) => this.onSubmit(model, body)}
                ref={(ref: any) => this.form = ref}
            />

        </Comp.Page.AdminPage>;
    }

    private getRecordId(props?: Readonly<CombinedProps>) {
        return parseInt(props
            ? props.match.params.id
            : this.props.match.params.id, 10);
    }

    private async handleDelete() {
        if (this._confirmDelete === null)
            return;

        const template = this._confirmDelete.getRef() as M.Email.EmailTemplate;
        if (template) {
            const result = await this.props.api.delete(Api.endpoints.admin.emailTemplate.template.withUrlParams({ id: template.id }));

            if (result.isSuccess) {
                this.props.removeTemplate(template);
                this.props.notifySuccess(`Email template <strong>${template.name}</strong> has been successfully deleted`);
                this.props.goto(Paths.AdminEmailTemplate.List);
            }
            else {
                this.props.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />"));
            }

            if (this._confirmDelete)
                this._confirmDelete.close();
        }
    }

    private lastPathIsFromListView() {
        const lastPath = this.props.lastPath;
        const path = Paths.AdminEmailTemplate.List;
        return lastPath && (lastPath === path || lastPath.indexOf(`${path}?`) === 0);
    }

    private async loadTemplate() {
        const id = this.getRecordId();
        const result = await this.props.api.get(Api.endpoints.admin.emailTemplate.template.withUrlParams({ id }));

        if (!this._isMounted)
            return;

        this.toggleLoading(false);

        if (result.isSuccess && result.data)
            this.props.updateTemplate(result.data);
        else if (result.errorCode === ErrorCodes.NotFound.id) {
            this.props.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />"));
            this.props.goto(Paths.AdminEmailTemplate.List);
        }
        else
            this.setErrors(M.Result.Result.errorsOrMessage(result));
    }

    private async loadTemplateFromState(isInitialCheck?: boolean) {
        const templateId = this.getRecordId();
        const template = this.props.templates.find((x: M.Email.EmailTemplate) => x.id === templateId);

        if (template) {
            await this.setStateAsync({ ...this.state, template });
            if (this.form)
                this.form.setFormData(template);
        }

        if (templateId > 0 && !template && isInitialCheck)
            this.toggleLoading(true, 0);
    }

    private async onSubmit(model: Forms.AdminEmailTemplate.TemplateData, body: any) {
        if (this.form)
            this.form.toggleProcessing(true);

        const result = await this.props.api.post(Api.endpoints.admin.emailTemplate.template.withUrlParams({ id: model.id }), {
            body: JSON.stringify(body),
        });

        if (!this._isMounted || !this.form)
            return;

        this.form.toggleProcessing(false);

        if (result.isSuccess) {
            this.props.notifySuccess(`Email template <strong>${model.name}</strong><br />has been successfully saved.`);
            this.props.goto(Paths.AdminEmailTemplate.List);
        }
        else {
            this.form.setFormErrors(M.Result.Result.errorsOrMessage(result), undefined, true);
        }
    }

    private toggleDeleteConfirmation() {
        if (this._confirmDelete !== null)
            this._confirmDelete.show(this.state.template);
    }
}

export const Edit: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(EditComponent) as any);



// WEBPACK FOOTER //
// ./src/containers/adminEmailTemplates/edit/Edit.tsx